import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import * as angular from 'angular';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { AppState } from '../../store';
import { selectAccountId } from '../../account/store/account.selectors';
import { Package, PackageTemplate } from '../package.models';
import { QueryParams } from '../../models/http.models';

@Injectable({
  providedIn: 'root',
})
export class PackagesResource {
  private apiRoot: string = `${process.env.API_URL}`;

  constructor(private http: HttpClient, private store: Store<AppState>) {}

  get(package_id: number, params?: QueryParams, account_id?: string): Observable<Package> {
    if (account_id) {
      return this.http.get<Package>(`${this.apiRoot}/${account_id}/api/packages/${package_id}`, { params });
    }

    return this.store.pipe(
      selectAccountId,
      mergeMap((store_account_id) =>
        this.http.get<Package>(`${this.apiRoot}/${store_account_id}/api/packages/${package_id}`, { params }),
      ),
    );
  }

  save(
    body: Partial<Package> | { source_package_id: number; source_package_name: string },
    params?: QueryParams,
  ): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => this.http.post<Package>(`${this.apiRoot}/${account_id}/api/packages`, body, { params })),
    );
  }

  update(package_id: number, body: Partial<Package>, params?: QueryParams): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.put<Package>(`${this.apiRoot}/${account_id}/api/packages/${package_id}`, body, { params }),
      ),
    );
  }

  remove(package_id: number, params?: QueryParams): Observable<any> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.delete<Package>(`${this.apiRoot}/${account_id}/api/packages/${package_id}`, { params }),
      ),
    );
  }

  query(params?: QueryParams): Observable<Package[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) => this.http.get<Package[]>(`${this.apiRoot}/${account_id}/api/packages`, { params })),
    );
  }

  search(params?: QueryParams): Observable<Package[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<Package[]>(`${this.apiRoot}/${account_id}/api/packages/search`, { params }),
      ),
    );
  }

  validate(package_id: number, params?: QueryParams): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.post<Package>(
          `${this.apiRoot}/${account_id}/api/packages/${package_id}/validations`,
          {},
          {
            params,
          },
        ),
      ),
    );
  }

  validateStatus(package_id: number, validation_id: number, params?: QueryParams): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<Package>(
          `${this.apiRoot}/${account_id}/api/packages/${package_id}/validations/${validation_id}`,
          { params },
        ),
      ),
    );
  }

  export(package_id: number, params?: QueryParams): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<Package>(`${this.apiRoot}/${account_id}/api/packages/${package_id}/export`, { params }),
      ),
    );
  }

  versions(package_id: number, params?: QueryParams): Observable<Package[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<Package[]>(`${this.apiRoot}/${account_id}/api/packages/${package_id}/snapshots`, { params }),
      ),
    );
  }

  version(package_id: number, version_id: number, params?: QueryParams): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<Package>(`${this.apiRoot}/${account_id}/api/packages/${package_id}/snapshots/${version_id}`, {
          params,
        }),
      ),
    );
  }

  majorVersion(package_id: number, body: Partial<Package>, params?: QueryParams): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.post<Package>(`${this.apiRoot}/${account_id}/api/packages/${package_id}/major_version`, body, {
          params,
        }),
      ),
    );
  }

  rollback(package_id: number, version_id: number, body?: Package, params?: QueryParams): Observable<Package> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.post<Package>(
          `${this.apiRoot}/${account_id}/api/packages/${package_id}/snapshots/${version_id}/rollback`,
          body,
          { params },
        ),
      ),
    );
  }

  templates(params?: QueryParams): Observable<PackageTemplate[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<PackageTemplate[]>(`${this.apiRoot}/${account_id}/api/packages/templates`, { params }),
      ),
    );
  }
}

((window as any).xplenty as angular.IModule).service('Package', downgradeInjectable(PackagesResource));
